/** Pure microphone-level math for the prejoin meter; no WebAudio dependencies. */

/**
 * Root-mean-square of the sample window, mapped to 0..1 and clamped. Non-finite samples
 * (NaN/Infinity, which analyser buffers can contain on some browsers) are skipped.
 */
export function rmsLevel(samples: Float32Array): number {
  let sum = 0;
  let counted = 0;
  for (const sample of samples) {
    if (Number.isFinite(sample)) {
      sum += sample * sample;
      counted += 1;
    }
  }
  if (counted === 0) {
    return 0;
  }
  return Math.min(1, Math.sqrt(sum / counted));
}

/**
 * Perceptual curve for the meter bar: the square root lifts quiet speech into the visible range
 * while keeping the value clamped to 0..1. Non-finite input maps to 0.
 */
export function meterRatio(level: number): number {
  if (!Number.isFinite(level)) {
    return 0;
  }
  return Math.min(1, Math.sqrt(Math.max(0, level)));
}

/**
 * Raw RMS mapped to dBFS for the prejoin meter's numeric readout: 20·log10(rms) rounded and
 * clamped to the display range [-48, 0]. Silence (rms 0 would be -Infinity) and NaN clamp to
 * the floor; over-range input clamps to the ceiling.
 */
export function levelDb(rms: number): number {
  if (Number.isNaN(rms) || rms <= 0) {
    return -48;
  }
  return Math.max(-48, Math.min(0, Math.round(20 * Math.log10(rms))));
}
